import React, {useEffect, useState, useRef} from "react";
import axios from "axios";
import {Calendar} from "primereact/calendar";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Toast} from "primereact/toast";
import {ScrollPanel} from "primereact/scrollpanel";
import {base, weekdays} from "../../api";
import {useTranslation} from "react-i18next";

export default function WeekDaysCalendar() {
	const {t} = useTranslation();
	const [dates, setDates] = useState([]);
	const [titleAM, setTitleAM] = useState("");
	const [titleEN, setTitleEN] = useState("");
	const [selectedDate, setSelectedDate] = useState(null);
	const [visible, setVisible] = useState(false);
	const [editId, setEditId] = useState(null);
	const toast = useRef(null);

	useEffect(() => {
		loadWeekDays();
	}, []);

	const loadWeekDays = () => {
		axios.get(base + weekdays).then(res => setDates(res.data));
	};

	const addDate = () => {
		if (!selectedDate || !titleAM || !titleEN) return;

		const existing = dates.find(d => d.date === selectedDate.toLocaleDateString("en-CA"));

		if (existing && !editId) {
			setEditId(existing.id);
		}

		const payload = {
			title_am: titleAM,
			title_en: titleEN,
			date: selectedDate.toLocaleDateString("en-CA"),
		};

		const req = editId
			? axios.patch(`${base}${weekdays}${editId}/`, payload)
			: axios.post(base + weekdays, payload);

		req.then(() => {
			loadWeekDays();
			toast.current.show({
				severity: "success",
				summary: "Success",
				detail: editId ? t("weekdays.success.updated") : t("weekdays.success.added"),
			});
			setVisible(false);
			setTitleAM("");
			setTitleEN("");
			setSelectedDate(null);
			setEditId(null);
		});
	};

	const deleteDate = id => {
		axios.delete(`${base}${weekdays}${id}/`).then(() => {
			loadWeekDays();
		});
	};

	const editDate = id => {
		const d = dates.find(i => i.id === id);
		if (!d) return;
		setTitleAM(d.title_am);
		setTitleEN(d.title_en);
		setSelectedDate(new Date(d.date));
		setEditId(id);
		setTimeout(() => setVisible(true), 0);
	};

	const highlightedDates = dates.map(d => new Date(d.date));

	return (
		<div className="p-4">
			<Toast ref={toast} />
			<div className="d-flex justify-content-between align-items-center my-3">
				<h5>{t("weekdays.title")}</h5>
				<Button
					label={t("weekdays.addNew")}
					rounded
					icon="pi pi-plus"
					onClick={() => setVisible(true)}
					className="mb-3"
				/>
			</div>

			<Dialog
				header={t("weekdays.dialogTitle")}
				visible={visible}
				className="col-lg-5 col-md-8 col-12 mx-auto"
				onHide={() => setVisible(false)}>
				<div className="p-3">
					<InputText
						placeholder={t("weekdays.titleAM")}
						value={titleAM}
						onChange={e => setTitleAM(e.target.value)}
						className="mb-4"
					/>
					<InputText
						placeholder={t("weekdays.titleEN")}
						value={titleEN}
						onChange={e => setTitleEN(e.target.value)}
						className="mb-4"
					/>
					<Calendar
						value={selectedDate}
						onChange={e => {
							if (!e.value) return;
							setSelectedDate(e.value);
						}}
						inline
						className="w-100"
						selectionMode="single"
					/>
				</div>
				<div className="p-3">
					<Button label={t("weekdays.add")} onClick={addDate} className="w-100 mx-auto mt-2 mb-3" />
				</div>
			</Dialog>

			<div className="b-radius b-shadow p-4">
				<div className="row mt-2">
					<div className="col-12 col-lg-6">
						<h6>{t("weekdays.highlightedCalendar")}</h6>
						<Calendar
							value={null}
							inline
							selectionMode="multiple"
							className="w-100"
							dateTemplate={date => {
								const isHighlighted = dates.some(d => {
									const d1 = new Date(d.date);
									return (
										d1.getDate() === date.day &&
										d1.getMonth() === date.month &&
										d1.getFullYear() === date.year
									);
								});
								return (
									<span
										style={{
											backgroundColor: isHighlighted ? "#3b82f6" : "transparent",
											color: isHighlighted ? "#ffffff" : "inherit",
											borderRadius: "50%",
											display: "inline-block",
											width: "2rem",
											height: "2rem",
											lineHeight: "2rem",
											textAlign: "center",
										}}>
										{date.day}
									</span>
								);
							}}
							onChange={e => {
								if (!e.value) return;
								const raw = Array.isArray(e.value) ? e.value[0] : e.value;
								const selected = new Date(raw);
								const selectedStr = selected.toISOString().split("T")[0];
								const existing = dates.find(d => d.date === selectedStr);
								if (existing) {
									setEditId(existing.id);
									setTitleAM(existing.title_am);
									setTitleEN(existing.title_en);
									setSelectedDate(new Date(existing.date));
								} else {
									setEditId(null);
									setTitleAM("");
									setTitleEN("");
									setSelectedDate(selected);
								}
								setTimeout(() => setVisible(true), 0);
							}}
						/>
					</div>

					<div className="col-12 col-lg-6">
						<h6>{t("weekdays.dates")}</h6>
						<ScrollPanel style={{height: "410px"}}>
							<div className="p-0 m-0">
								{[...dates].reverse().map(date => (
									<div
										key={date.id}
										className="d-flex justify-content-between align-items-center border-bottom py-2">
										<div>
											<span className="fw-bold text-dark">{date.date}</span> - {date.title_am} (
											{date.title_en})
										</div>
										<div>
											<Button
												icon="pi pi-pencil"
												text
												className="ms-2"
												onClick={() => editDate(date.id)}
											/>
											<Button
												icon="pi pi-trash"
												text
												className="ms-2"
												severity="danger"
												onClick={() => deleteDate(date.id)}
											/>
										</div>
									</div>
								))}
							</div>
						</ScrollPanel>
					</div>
				</div>
			</div>
		</div>
	);
}
